import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { inject, observer } from 'mobx-react'
import { toJS } from 'mobx'
import PropTypes from 'prop-types'
import {
  Formik,
  Form,
  Field,
  FastField,
  ErrorMessage,
  FieldArray
} from 'formik'
import RichTextEditor from '@components/Form/RichTextEditor'
import Input from '@components/Form/Input'
import Button from '@components/Button'
import RepeatableAction from '@components/Form/RepeatableAction'
import Checkbox from '@components/Checkbox'
import AsyncSelect from 'react-select/async'
import Select from 'react-select'
import AsyncCreatable from 'react-select/async-creatable'
import { expertAutocompleteApi } from '@atheneum/api'
import { debounce, differenceBy, findIndex, get, uniq } from 'lodash'
import Tooltip from '@components/Tooltip/Tooltip'
import Lusha from '@components/Icon/Lusha'
import Action from '@components/Action'
import DeleteIcon from '@components/Icon/Delete'
import Spinner from '@components/spinner'
import PlusIcon from '@components/Icon/PlusIcon'
import moment from 'moment'
import { stringifyDate } from '@atheneum/api/dist/lib'
import MonthYearPickerRange from '@components/MonthPicker/MonthYearPickerRange'
import GetContactInfo from '../../ExpertPage/Components/GetContactInfo'
import { ExpertOptionField } from './helpers/expertOptionField'
import { ExpertProcessingStatus } from './helpers/expertProcessingStatus'

import ArrowDownwardRounded from '@material-ui/icons/ArrowDownwardRounded'
import ArrowUpwardRounded from '@material-ui/icons/ArrowUpwardRounded'

const createEmptyLanguageObject = () => ({
  id: '',
  languageToUser: { languageId: '', languageProficiencyId: '' }
})

const createEmptyWorkExperienceObject = () => ({
  company: { name: '', id: '' },
  current: false,
  description: '',
  startDate: '',
  endDate: '',
  position: { id: '', name: '' },
  location: ''
})

const createEmptyPhoneObject = () => ({
  phoneTypeId: null,
  phoneNum: ''
})

const createEmptyEmailObject = () => ({
  email: ''
})

const createEmptyLinkObject = () => ({
  professionalLinkUrl: ''
})

function ContactInfoEntry({ item, itemToString, onClick }) {
  return (
    <div key={itemToString(item)} className="contact-info-picker__entry">
      <PlusIcon onClick={() => onClick(item)} />
      <span>{itemToString(item)}</span>
    </div>
  )
}

ContactInfoEntry.propTypes = {
  item: PropTypes.object,
  itemToString: PropTypes.func,
  onClick: PropTypes.func
}

class ExpertBulkCreate extends Component {
  static propTypes = {
    staticData: PropTypes.object,
    modalStore: PropTypes.object,
    growlStore: PropTypes.object,
    expertStore: PropTypes.object,
    expertSearchStore: PropTypes.object,
    expertBulkCreateStore: PropTypes.object,
    userStore: PropTypes.object,
    expertsToProcess: PropTypes.object,
    processingBulkId: PropTypes.string,
    requiredFieldOptions: PropTypes.object
  }

  constructor(props) {
    super(props)

    this.formikRef = React.createRef()
    this.firstValidate = true
    this.runLongLastingValidation = true

    this.state = {
      showGetContactInfoSectionForTrackingIds: [],
      addedPhonesByTrackingId: [],
      addedEmailsByTrackingId: [],
      expertErrors: [],
      showScrollUp: false,
      showScrollDown: false,
      leftBoundForRightColumn: 0,
      scrollUpTop: 0,
      scrollDownTop: 0
    }
  }

  handleResize = event => {
    // Element selectors
    const overlay = document.querySelector('.modal__overlay')
    const overlayComputedStyle = getComputedStyle(overlay)
    const content = document.querySelector('.modal__content')
    const upButton = document.querySelector(
      '.expert-bulk-create__navigation-button--up'
    )
    const downButton = document.querySelector(
      '.expert-bulk-create__navigation-button--down'
    )

    // Calculate margin width on one side
    const marginWidth = (overlay.clientWidth - content.clientWidth) / 2

    const leftBoundForRightColumn = content.clientWidth + marginWidth
    const scrollUpTop = 0
    const scrollDownTop = overlay.clientHeight - downButton.clientHeight
    let showScrollUp = false
    let showScrollDown = false

    // Button show logic
    const pageHeight = overlay.scrollHeight - overlay.clientHeight
    const topEnableBound =
      pageHeight - parseFloat(overlayComputedStyle.paddingTop)
    const bottomEnableBound = parseFloat(overlayComputedStyle.paddingBottom)
    const currentScroll =
      overlay.scrollHeight - overlay.scrollTop - overlay.clientHeight

    if (currentScroll < topEnableBound) {
      showScrollUp = true
      upButton.classList.remove(
        'expert-bulk-create__navigation-button--up__hidden'
      )
    } else {
      showScrollUp = false
      upButton.classList.add(
        'expert-bulk-create__navigation-button--up__hidden'
      )
    }

    if (currentScroll > bottomEnableBound) {
      showScrollDown = true
      downButton.classList.remove(
        'expert-bulk-create__navigation-button--down__hidden'
      )
    } else {
      showScrollDown = false
      downButton.classList.add(
        'expert-bulk-create__navigation-button--down__hidden'
      )
    }

    this.setState((prevState, props) => {
      return {
        leftBoundForRightColumn: leftBoundForRightColumn,
        scrollUpTop: scrollUpTop,
        scrollDownTop: scrollDownTop,
        showScrollUp: showScrollUp,
        showScrollDown: showScrollDown
      }
    })
  }

  handleKeyDown = event => {
    if (event.key === 'Escape') {
      this.props.expertsToProcess.forEach((expert, expertIndex) => {
        this.props.expertBulkCreateStore.assignProcessingStatus(
          expert.bulkCreateTrackingId,
          this.props.processingBulkId,
          ExpertProcessingStatus.Cancelled,
          '',
          toJS(expert),
          expert.bulkCreateCustomIdentifier
        )
      })

      this.props.modalStore.close()
    } else if (event.key === 'Enter') {
      this.runLongLastingValidation = true
    }
  }

  componentDidMount() {
    // Add event listeners
    const overlay = document.querySelector('.modal__overlay')
    overlay.addEventListener('scroll', this.handleScroll)

    window.addEventListener('resize', this.handleResize)

    overlay.addEventListener('keydown', this.handleKeyDown)

    // Force handleResize to set initial button positions
    this.handleResize()
  }

  componentWillUnmount() {
    // Remove event listeners
    const overlay = document.querySelector('.modal__overlay')
    overlay.removeEventListener('scroll', this.handleScroll)

    window.removeEventListener('resize', this.handleResize)

    overlay.removeEventListener('keydown', this.handleKeyDown)
  }

  handleScroll = event => {
    const { scrollHeight, scrollTop, clientHeight } = event.srcElement
    const computedStyle = getComputedStyle(event.srcElement)

    const pageHeight = scrollHeight - clientHeight

    const topEnableBound = pageHeight - parseFloat(computedStyle.paddingTop)
    const bottomEnableBound = parseFloat(computedStyle.paddingBottom)

    const currentScroll = scrollHeight - scrollTop - clientHeight

    if (currentScroll < topEnableBound) {
      if (!this.state.showScrollUp) {
        this.state.showScrollUp = true
        const upButton = document.querySelector(
          '.expert-bulk-create__navigation-button--up'
        )
        upButton.classList.remove(
          'expert-bulk-create__navigation-button--up__hidden'
        )
      }
    } else {
      if (this.state.showScrollUp) {
        this.state.showScrollUp = false
        const upButton = document.querySelector(
          '.expert-bulk-create__navigation-button--up'
        )
        upButton.classList.add(
          'expert-bulk-create__navigation-button--up__hidden'
        )
      }
    }

    if (currentScroll > bottomEnableBound) {
      if (!this.state.showScrollDown) {
        this.state.showScrollDown = true
        const downButton = document.querySelector(
          '.expert-bulk-create__navigation-button--down'
        )
        downButton.classList.remove(
          'expert-bulk-create__navigation-button--down__hidden'
        )
      }
    } else {
      if (this.state.showScrollDown) {
        this.state.showScrollDown = false
        const downButton = document.querySelector(
          '.expert-bulk-create__navigation-button--down'
        )
        downButton.classList.add(
          'expert-bulk-create__navigation-button--down__hidden'
        )
      }
    }
  }

  getCitySuggestionsDebounced = debounce((inputText, callback) => {
    expertAutocompleteApi
      .getCitySuggestions(inputText)
      .then(options =>
        callback(
          options.map(e => ({ label: e.description, value: e.place_id }))
        )
      )
  }, 200)

  getPositionSuggestionsDebounced = debounce((inputText, callback) => {
    expertAutocompleteApi
      .getPositionSuggestions(inputText, 8)
      .then(options =>
        callback(options.map(e => ({ label: e.name, value: e.id })))
      )
  }, 200)

  getCompanySuggestionsDebounced = debounce((inputText, callback) => {
    expertAutocompleteApi
      .getCompanySuggestions(inputText)
      .then(options =>
        callback(options.map(e => ({ label: e.name, value: e.id })))
      )
  }, 200)

  getCvSourceSuggestionsDebounced = debounce((inputText, callback) => {
    expertAutocompleteApi
      .getCvSourceSuggestions(inputText)
      .then(options =>
        callback(options.map(e => ({ label: e.name, value: e.id })))
      )
  }, 200)

  renderFormikError = fieldPath => {
    return (
      <ErrorMessage
        name={fieldPath}
        render={msg => <div className="expert-form__input-error">{msg}</div>}
      />
    )
  }

  getLocationFromSuggestion = async selectedOption => {
    if (selectedOption && selectedOption.value) {
      const response = await expertAutocompleteApi.getPlaceInfoById(
        selectedOption.value
      )

      const extractedResponseResult = expertAutocompleteApi.extractPlaceInfo(
        response
      )

      const timezoneResponseResult = await expertAutocompleteApi.getTimezone(
        response.geometry.location.lat,
        response.geometry.location.lng
      )

      const extractedResponse = extractedResponseResult?.value
      const timezone = timezoneResponseResult?.value

      const matchedTimezone = this.props.staticData.timezones.find(
        e => e.name == timezone?.timeZoneId
      )
      const matchedCountry = this.props.staticData.countries.find(
        e => e.id == extractedResponse.countryId
      )

      return {
        address: extractedResponse.address,
        city: extractedResponse.city,
        state: extractedResponse.state,
        countryId: extractedResponse.countryId,
        country: matchedCountry,
        timezoneId: matchedTimezone
          ? matchedTimezone.id
          : extractedResponse.countryDefaultTimezoneId,
        postalCode: extractedResponse.postalCode
      }
    }
    return null
  }

  renderIndustrySubindustryItem = industryOrSubindustry => {
    const isIndustry = industryOrSubindustry.type === 'industry'
    if (isIndustry) {
      return (
        <div>
          <b>{industryOrSubindustry.name}</b>
        </div>
      )
    }

    return (
      <div className="autocomplete__item--suboption">
        {industryOrSubindustry.name}
      </div>
    )
  }

  renderPersonalInformation = (
    expert,
    expertIndex,
    errors,
    setFieldValue,
    setFieldTouched,
    validateForm
  ) => {
    if (
      !this.props.confirmFieldOptions.some(
        e =>
          e === ExpertOptionField.Title ||
          e === ExpertOptionField.FirstName ||
          e === ExpertOptionField.LastName ||
          e === ExpertOptionField.OriginalName
      ) &&
      (!this.state.expertErrors[expertIndex] ||
        !this.state.expertErrors[expertIndex].some(
          e =>
            e === ExpertOptionField.Title ||
            e === ExpertOptionField.FirstName ||
            e === ExpertOptionField.LastName ||
            e === ExpertOptionField.OriginalName
        ))
    ) {
      return
    }

    return (
      <div className="expert-form__section">
        <h2>Personal information</h2>
        {(this.props.confirmFieldOptions.includes(ExpertOptionField.Title) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.Title
            ))) && (
          <div className="expert-form__input-group">
            <label className="expert-form__label">Title</label>
            <div className={'expert-form__input-wrapper'}>
              <FastField name={`experts.${expertIndex}.title`}>
                {({ field, form, meta }) => (
                  <Select
                    placeholder="Select title"
                    options={this.props.staticData.titles.map(e => {
                      return { label: e, value: e }
                    })}
                    onChange={async selectedValue => {
                      await setFieldValue(
                        `experts.${expertIndex}.title`,
                        selectedValue.value
                      )
                      setFieldTouched(`experts.${expertIndex}.title`)
                    }}
                    value={{ label: expert.title, value: expert.title }}
                    isClearable={false}
                    className={
                      meta.error && meta.touched
                        ? 'expert-bulk-create__dropdown-wrapper expert-bulk-create__dropdown-wrapper--error'
                        : 'expert-bulk-create__dropdown-wrapper'
                    }
                    classNamePrefix="expert-bulk-create__dropdown"
                  />
                )}
              </FastField>
              {this.renderFormikError(`experts.${expertIndex}.title`)}
            </div>
          </div>
        )}
        {(this.props.confirmFieldOptions.includes(
          ExpertOptionField.FirstName
        ) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.FirstName
            ))) && (
          <Field
            name={`experts.${expertIndex}.firstName`}
            label="First name"
            onChange={e => {
              setFieldValue(`experts.${expertIndex}.firstName`, e.target.value)
              setFieldValue(
                `experts.${expertIndex}.fullName`,
                (e.target.value + ' ' + expert.lastName).trim()
              )
            }}
            onBlur={() => {
              setFieldTouched(`experts.${expertIndex}.firstName`)
              setFieldTouched(`experts.${expertIndex}.fullName`)
            }}
            component={Input}
          />
        )}
        {(this.props.confirmFieldOptions.includes(ExpertOptionField.LastName) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.LastName
            ))) && (
          <Field
            name={`experts.${expertIndex}.lastName`}
            label="Last name"
            onChange={e => {
              setFieldValue(`experts.${expertIndex}.lastName`, e.target.value)
              setFieldValue(
                `experts.${expertIndex}.fullName`,
                (expert.firstName + ' ' + e.target.value).trim()
              )
            }}
            onBlur={() => {
              setFieldTouched(`experts.${expertIndex}.lastName`)
              setFieldTouched(`experts.${expertIndex}.fullName`)
            }}
            component={Input}
          />
        )}
        {(this.props.confirmFieldOptions.includes(
          ExpertOptionField.OriginalName
        ) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.OriginalName
            ))) && (
          <FastField
            name={`experts.${expertIndex}.originalName`}
            label="Original name"
            component={Input}
          />
        )}
      </div>
    )
  }

  renderLocation = (
    expert,
    expertIndex,
    errors,
    setFieldValue,
    setFieldTouched,
    validateForm
  ) => {
    if (
      !this.props.confirmFieldOptions.some(
        e =>
          e === ExpertOptionField.City ||
          e === ExpertOptionField.State ||
          e === ExpertOptionField.Country ||
          e === ExpertOptionField.Timezone
      ) &&
      (!this.state.expertErrors[expertIndex] ||
        !this.state.expertErrors[expertIndex].some(
          e =>
            e === ExpertOptionField.City ||
            e === ExpertOptionField.State ||
            e === ExpertOptionField.Country ||
            e === ExpertOptionField.Timezone
        ))
    ) {
      return
    }

    return (
      <div className="expert-form__section">
        <h2>Location</h2>
        <div className="expert-form__input-group">
          <AsyncSelect
            isSearchable={true}
            isClearable={true}
            loadOptions={this.getCitySuggestionsDebounced}
            onChange={async selectedOption => {
              if (selectedOption) {
                const locationResult = await this.getLocationFromSuggestion(
                  selectedOption
                )

                await Promise.all([
                  setFieldValue(
                    `experts.${expertIndex}.address.address1`,
                    locationResult.address
                  ),
                  setFieldValue(
                    `experts.${expertIndex}.address.city`,
                    locationResult.city
                  ),
                  setFieldValue(
                    `experts.${expertIndex}.address.state`,
                    locationResult.state
                  ),
                  setFieldValue(
                    `experts.${expertIndex}.country`,
                    locationResult.country.name
                  ),
                  setFieldValue(
                    `experts.${expertIndex}.address.countryId`,
                    locationResult.countryId
                  ),
                  setFieldValue(
                    `experts.${expertIndex}.address.timezoneId`,
                    locationResult.timezoneId
                  ),
                  setFieldValue(
                    `experts.${expertIndex}.address.postalCode`,
                    locationResult.postalCode
                  )
                ])

                setFieldTouched(`experts.${expertIndex}.address.address1`)
                setFieldTouched(`experts.${expertIndex}.address.city`)
                setFieldTouched(`experts.${expertIndex}.address.state`)
                setFieldTouched(`experts.${expertIndex}.country`)
                setFieldTouched(`experts.${expertIndex}.address.countryId`)
                setFieldTouched(`experts.${expertIndex}.address.timezoneId`)
                setFieldTouched(`experts.${expertIndex}.address.postalCode`)
              }
            }}
            loadingMessage={() => 'Loading places...'}
            placeholder={'Location quick select'}
            className="expert-form__input"
          />
        </div>
        <FastField
          name={`experts.${expertIndex}.address.city`}
          label="City"
          component={Input}
        />
        <FastField
          name={`experts.${expertIndex}.address.state`}
          label="State"
          component={Input}
        />
        <div className="expert-form__input-group">
          <label className="expert-form__label">Country</label>
          <div className={'expert-form__input-wrapper'}>
            <FastField name={`experts.${expertIndex}.address.countryId`}>
              {({ field, form, meta }) => (
                <Select
                  options={this.props.staticData.countries.map(e => {
                    return { value: e.name, label: e.name }
                  })}
                  onChange={async selectedValue => {
                    if (selectedValue) {
                      const matchedCountry = this.props.staticData.countries.find(
                        e => e.name === selectedValue.value
                      )

                      await Promise.all([
                        setFieldValue(
                          `experts.${expertIndex}.country`,
                          selectedValue.value
                        ),
                        setFieldValue(
                          `experts.${expertIndex}.address.countryId`,
                          matchedCountry.id
                        )
                      ])
                    } else {
                      await Promise.all([
                        setFieldValue(`experts.${expertIndex}.country`, null),
                        setFieldValue(
                          `experts.${expertIndex}.address.countryId`,
                          null
                        )
                      ])
                    }
                    setFieldTouched(`experts.${expertIndex}.country`)
                    setFieldTouched(`experts.${expertIndex}.address.countryId`)
                  }}
                  isClearable={true}
                  value={(() => {
                    if (expert.address.countryId) {
                      const matchedCountry = this.props.staticData.countries.find(
                        e => e.id === expert.address.countryId
                      )
                      return {
                        value: expert.address.countryId,
                        label: matchedCountry.name
                      }
                    }
                  })()}
                  className={
                    meta.error && meta.touched
                      ? 'expert-bulk-create__dropdown-wrapper expert-bulk-create__dropdown-wrapper--error'
                      : 'expert-bulk-create__dropdown-wrapper'
                  }
                  classNamePrefix="expert-bulk-create__dropdown"
                />
              )}
            </FastField>
            {this.renderFormikError(`experts.${expertIndex}.address.countryId`)}
          </div>
        </div>
        <div className="expert-form__input-group">
          <label className="expert-form__label">Timezone</label>
          <div className={'expert-form__input-wrapper'}>
            <FastField name={`experts.${expertIndex}.address.timezoneId`}>
              {({ field, form, meta }) => (
                <Select
                  options={this.props.staticData.timezones.map(e => {
                    return { value: e.id, label: e.name }
                  })}
                  onChange={async selectedValue => {
                    if (selectedValue) {
                      await setFieldValue(
                        `experts.${expertIndex}.address.timezoneId`,
                        selectedValue.value
                      )
                    } else {
                      await setFieldValue(
                        `experts.${expertIndex}.address.timezoneId`,
                        null
                      )
                    }
                    setFieldTouched(`experts.${expertIndex}.address.timezoneId`)
                  }}
                  isClearable={true}
                  value={(() => {
                    if (expert.address.timezoneId) {
                      const matchedTimezone = this.props.staticData.timezones.find(
                        e => e.id === expert.address.timezoneId
                      )
                      return {
                        value: expert.address.timezoneId,
                        label: matchedTimezone.name
                      }
                    }
                  })()}
                  className={
                    meta.error && meta.touched
                      ? 'expert-bulk-create__dropdown-wrapper expert-bulk-create__dropdown-wrapper--error'
                      : 'expert-bulk-create__dropdown-wrapper'
                  }
                  classNamePrefix="expert-bulk-create__dropdown"
                />
              )}
            </FastField>
            {this.renderFormikError(
              `experts.${expertIndex}.address.timezoneId`
            )}
          </div>
        </div>
      </div>
    )
  }

  renderProfessionalInformation = (
    expert,
    expertIndex,
    errors,
    setFieldValue,
    setFieldTouched,
    validateForm
  ) => {
    if (
      !this.props.confirmFieldOptions.some(
        e =>
          e === ExpertOptionField.Industry ||
          e === ExpertOptionField.Language ||
          e === ExpertOptionField.IndustryExperience ||
          e === ExpertOptionField.ExpertIndustryExperience ||
          e === ExpertOptionField.HourlyRate ||
          e === ExpertOptionField.CurrentUsGovernmentEmployee
      ) &&
      (!this.state.expertErrors[expertIndex] ||
        !this.state.expertErrors[expertIndex].some(
          e =>
            e === ExpertOptionField.Industry ||
            e === ExpertOptionField.Language ||
            e === ExpertOptionField.IndustryExperience ||
            e === ExpertOptionField.ExpertIndustryExperience ||
            e === ExpertOptionField.HourlyRate ||
            e === ExpertOptionField.CurrentUsGovernmentEmployee
        ))
    ) {
      return
    }

    const isAdminOrERM = this.props.userStore.user.permissions.find(
      p =>
        p.id === this.props.staticData.CONST.PERMISSIONS.ADMIN ||
        p.id ===
          this.props.staticData.CONST.PERMISSIONS.ADVANCED_EXPERT_MANAGEMENT
    )
    return (
      <div className="expert-form__section">
        <h2>Professional information</h2>
        {(this.props.confirmFieldOptions.includes(ExpertOptionField.Industry) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.Industry
            ))) && (
          <div className="expert-form__input-group">
            <label className="expert-form__label">Industry / SubIndustry</label>
            <div className={'expert-form__input-wrapper'}>
              <FastField name={`experts.${expertIndex}.expert.subIndustries`}>
                {({ field, form, meta }) => (
                  <Select
                    options={this.props.staticData.industries.map(industry => {
                      return {
                        label: industry.name,
                        options: this.props.staticData.subIndustries
                          .filter(
                            subIndustry =>
                              subIndustry.industryId === industry.id
                          )
                          .map(subIndustry => {
                            return {
                              label: subIndustry.name,
                              value: subIndustry,
                              isDisabled:
                                subIndustry.id ===
                                  this.props.staticData.CONST.SUB_INDUSTRIES
                                    .OTHER ||
                                expert.expert.subIndustries.some(
                                  e => e.id === subIndustry.id
                                )
                            }
                          })
                      }
                    })}
                    isMulti={true}
                    onChange={async selectedValue => {
                      if (selectedValue) {
                        await setFieldValue(
                          `experts.${expertIndex}.expert.subIndustries`,
                          selectedValue
                            .filter(
                              e =>
                                e.value.id !==
                                this.props.staticData.CONST.SUB_INDUSTRIES.OTHER
                            )
                            .map(e => e.value)
                        )
                      } else {
                        await setFieldValue(
                          `experts.${expertIndex}.expert.subIndustries`,
                          []
                        )
                      }
                      setFieldTouched(
                        `experts.${expertIndex}.expert.subIndustries`
                      )
                    }}
                    value={(() => {
                      return expert.expert.subIndustries.map(subIndustry => {
                        return {
                          label: subIndustry.name,
                          value: subIndustry
                        }
                      })
                    })()}
                    className={
                      meta.error && meta.touched
                        ? 'expert-bulk-create__dropdown-wrapper expert-bulk-create__dropdown-wrapper--error'
                        : 'expert-bulk-create__dropdown-wrapper'
                    }
                    classNamePrefix="expert-bulk-create__dropdown"
                  />
                )}
              </FastField>
              {this.renderFormikError(
                `experts.${expertIndex}.expert.subIndustries`
              )}
            </div>
          </div>
        )}
        {(this.props.confirmFieldOptions.includes(ExpertOptionField.Language) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.Language
            ))) && (
          <div className="expert-form__input-group">
            <label className="expert-form__label">Languages</label>
            <div className="expert-form__input-wrapper">
              <FieldArray
                name={`experts.${expertIndex}.languages`}
                render={languageArrayHelpers => (
                  <div className="expert-form__repeatable-input-wrapper">
                    {expert.languages.map((language, languageIndex) => (
                      <div
                        className="expert-form__repeatable-input expert-form__repeatable-input--multiple"
                        key={languageIndex}>
                        <div className={'expert-form__input-wrapper'}>
                          <FastField
                            name={`experts.${expertIndex}.languages.${languageIndex}.id`}>
                            {({ field, form, meta }) => (
                              <Select
                                options={this.props.staticData.languages.map(
                                  language => {
                                    return {
                                      label: language.name,
                                      value: language.id,
                                      isDisabled: expert.languages.some(
                                        e => e.id === language.id
                                      )
                                    }
                                  }
                                )}
                                onChange={async selectedValue => {
                                  if (selectedValue) {
                                    const matchedLanguage = this.props.staticData.languages.find(
                                      e => e.id === selectedValue.value
                                    )
                                    await Promise.all([
                                      setFieldValue(
                                        `experts.${expertIndex}.languages.${languageIndex}.id`,
                                        selectedValue.value
                                      ),
                                      setFieldValue(
                                        `experts.${expertIndex}.languages.${languageIndex}.name`,
                                        matchedLanguage.name
                                      ),
                                      setFieldValue(
                                        `experts.${expertIndex}.languages.${languageIndex}.languageToUser.languageId`,
                                        selectedValue.value
                                      )
                                    ])

                                    setFieldTouched(
                                      `experts.${expertIndex}.languages.${languageIndex}.id`
                                    )
                                    setFieldTouched(
                                      `experts.${expertIndex}.languages.${languageIndex}.name`
                                    )
                                    setFieldTouched(
                                      `experts.${expertIndex}.languages.${languageIndex}.languageToUser.languageId`
                                    )
                                  }
                                }}
                                value={(() => {
                                  return {
                                    label: language.name,
                                    value: language.id
                                  }
                                })()}
                                className={
                                  meta.error && meta.touched
                                    ? 'expert-bulk-create__dropdown-wrapper expert-bulk-create__dropdown-wrapper--error'
                                    : 'expert-bulk-create__dropdown-wrapper'
                                }
                                classNamePrefix="expert-bulk-create__dropdown"
                              />
                            )}
                          </FastField>
                          {this.renderFormikError(
                            `experts.${expertIndex}.languages.${languageIndex}.id`
                          )}
                        </div>

                        <div className={'expert-form__input-wrapper'}>
                          <FastField
                            name={`experts.${expertIndex}.languages.${languageIndex}.languageToUser.languageProficiencyId`}>
                            {({ field, form, meta }) => (
                              <Select
                                options={this.props.staticData.languageProficiencies.map(
                                  languageProficiency => {
                                    return {
                                      label: languageProficiency.name,
                                      value: languageProficiency.id
                                    }
                                  }
                                )}
                                onChange={async selectedValue => {
                                  if (selectedValue) {
                                    await setFieldValue(
                                      `experts.${expertIndex}.languages.${languageIndex}.languageToUser.languageProficiencyId`,
                                      selectedValue.value
                                    )

                                    setFieldTouched(
                                      `experts.${expertIndex}.languages.${languageIndex}.languageToUser.languageProficiencyId`
                                    )
                                  }
                                }}
                                value={(() => {
                                  if (
                                    language.languageToUser
                                      ?.languageProficiencyId
                                  ) {
                                    const matchedLanguageProficiency = this.props.staticData.languageProficiencies.find(
                                      e =>
                                        e.id ===
                                        language.languageToUser
                                          .languageProficiencyId
                                    )
                                    return {
                                      label: matchedLanguageProficiency.name,
                                      value: matchedLanguageProficiency.id
                                    }
                                  }
                                })()}
                                className={
                                  meta.error && meta.touched
                                    ? 'expert-bulk-create__dropdown-wrapper expert-bulk-create__dropdown-wrapper--error'
                                    : 'expert-bulk-create__dropdown-wrapper'
                                }
                                classNamePrefix="expert-bulk-create__dropdown"
                              />
                            )}
                          </FastField>
                          {this.renderFormikError(
                            `experts.${expertIndex}.languages.${languageIndex}.languageToUser.languageProficiencyId`
                          )}
                        </div>
                        <RepeatableAction
                          fieldValues={expert.languages}
                          arrayHelpers={languageArrayHelpers}
                          index={languageIndex}
                          allowAdd={expert.languages.every(
                            e =>
                              e.id &&
                              e.name &&
                              e.languageToUser?.languageId &&
                              e.languageToUser.languageProficiencyId
                          )}
                          initialObject={createEmptyLanguageObject()}
                          onAfterDelete={() => {
                            validateForm()
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              />
            </div>
          </div>
        )}
        {(this.props.confirmFieldOptions.includes(
          ExpertOptionField.IndustryExperience
        ) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.IndustryExperience
            ))) && (
          <FastField
            name={`experts.${expertIndex}.expert.industryExpertise`}
            label="Industry experience"
            placeholder="Industry experience that will be visible on the Client interface and not visible to Expert"
            toolbarRight
            component={RichTextEditor}
          />
        )}
        {(this.props.confirmFieldOptions.includes(
          ExpertOptionField.ExpertIndustryExperience
        ) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.ExpertIndustryExperience
            ))) && (
          <FastField
            name={`experts.${expertIndex}.expert.expertIndustryExperience`}
            label={
              <div>
                Expert industry experience
                <Tooltip tooltip="Cannot edit expert experience. This information is present on expert interface and is fully manageable by experts. If you require changes, please contact ERM team." />
              </div>
            }
            placeholder="Expert industry experinece will be visible to Expert"
            toolbarRight
            disabled={!isAdminOrERM}
            component={RichTextEditor}
          />
        )}
        {(this.props.confirmFieldOptions.includes(
          ExpertOptionField.HourlyRate
        ) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.HourlyRate
            ))) && (
          <div className="expert-form__input-group">
            <label className="expert-form__label">Hourly rate</label>
            <div className="expert-form__input-wrapper expert-form__input-wrapper--two_one_children">
              <div className={'expert-form__input-wrapper'}>
                <FastField
                  name={`experts.${expertIndex}.expert.hourlyRateCurrencyId`}>
                  {({ field, form, meta }) => (
                    <Select
                      placeholder="Currency"
                      options={this.props.staticData.currencies.map(
                        currency => {
                          return {
                            label: currency.name,
                            value: currency.id
                          }
                        }
                      )}
                      onChange={async selectedValue => {
                        await setFieldValue(
                          `experts.${expertIndex}.expert.hourlyRateCurrencyId`,
                          selectedValue.value
                        )

                        setFieldTouched(
                          `experts.${expertIndex}.expert.hourlyRateCurrencyId`
                        )
                      }}
                      value={(() => {
                        if (expert.expert?.hourlyRateCurrencyId) {
                          const matchedCurreny = this.props.staticData.currencies.find(
                            currency =>
                              currency.id === expert.expert.hourlyRateCurrencyId
                          )
                          return {
                            label: matchedCurreny.name,
                            value: matchedCurreny.id
                          }
                        }
                      })()}
                      className={
                        meta.error && meta.touched
                          ? 'expert-bulk-create__dropdown-wrapper expert-bulk-create__dropdown-wrapper--error'
                          : 'expert-bulk-create__dropdown-wrapper'
                      }
                      classNamePrefix="expert-bulk-create__dropdown"
                    />
                  )}
                </FastField>
                {this.renderFormikError(
                  `experts.${expertIndex}.expert.hourlyRateCurrencyId`
                )}
              </div>
              <FastField
                name={`experts.${expertIndex}.expert.hourlyRate`}
                placeholder="Amount"
                component={Input}
              />
            </div>
          </div>
        )}
        {(this.props.confirmFieldOptions.includes(
          ExpertOptionField.CurrentUsGovernmentEmployee
        ) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.CurrentUsGovernmentEmployee
            ))) && (
          <div className="expert-form__input-group">
            <FastField
              label="Current US Government employee"
              onChange={value => {
                setFieldValue(
                  `experts.${expertIndex}.expert.currentUsGovernmentEmployee`,
                  value
                )
              }}
              onBlur={() => {
                setFieldTouched(
                  `experts.${expertIndex}.expert.currentUsGovernmentEmployee`
                )
              }}
              value={expert.expert?.currentUsGovernmentEmployee}
              component={Checkbox}
            />
          </div>
        )}
      </div>
    )
  }

  renderWorkExperience = (
    expert,
    expertIndex,
    errors,
    setFieldValue,
    setFieldTouched,
    validateForm
  ) => {
    if (
      !this.props.confirmFieldOptions.some(
        e => e === ExpertOptionField.WorkExperience
      ) &&
      (!this.state.expertErrors[expertIndex] ||
        !this.state.expertErrors[expertIndex].some(
          e => e === ExpertOptionField.WorkExperience
        ))
    ) {
      return
    }

    return (
      <div>
        <FieldArray
          name={`experts.${expertIndex}.experiences`}
          render={experienceArrayHelpers => (
            <div>
              {expert.experiences.map((experience, experienceIndex) => (
                <div className="expert-form__section" key={experienceIndex}>
                  <h2 className="expert-form__subtitle  expert-form__subtitle--with-action">
                    {experienceIndex > 0
                      ? `Work experience #${experienceIndex + 1}`
                      : `Work experience`}
                    {expert.experiences.length > 1 && (
                      <div className="action-wrapper">
                        <Action
                          className="expert-form__repeatable-action--delete"
                          onClick={() => {
                            experienceArrayHelpers.remove(experienceIndex)
                          }}>
                          <DeleteIcon blue />
                        </Action>
                      </div>
                    )}
                  </h2>
                  <div className="expert-form__input-group">
                    <label className="expert-form__label">Position *</label>
                    <div className={'expert-form__input-wrapper'}>
                      <FastField
                        name={`experts.${expertIndex}.experiences.${experienceIndex}.position`}>
                        {({ field, form, meta }) => (
                          <AsyncCreatable
                            isSearchable={true}
                            isClearable={true}
                            loadOptions={this.getPositionSuggestionsDebounced}
                            onChange={async selectedOption => {
                              if (selectedOption) {
                                await setFieldValue(
                                  `experts.${expertIndex}.experiences.${experienceIndex}.position`,
                                  {
                                    id: selectedOption.value,
                                    name: selectedOption.label
                                  }
                                )
                              } else {
                                await setFieldValue(
                                  `experts.${expertIndex}.experiences.${experienceIndex}.position`,
                                  null
                                )
                              }

                              setFieldTouched(
                                `experts.${expertIndex}.experiences.${experienceIndex}.position`
                              )
                            }}
                            onCreateOption={async valueToCreate => {
                              if (valueToCreate) {
                                await setFieldValue(
                                  `experts.${expertIndex}.experiences.${experienceIndex}.position`,
                                  { id: valueToCreate, name: valueToCreate }
                                )

                                setFieldTouched(
                                  `experts.${expertIndex}.experiences.${experienceIndex}.position`
                                )
                              }
                            }}
                            value={{
                              label: experience.position?.name,
                              value: {
                                id: experience.position?.id
                                  ? experience.position?.id
                                  : experience.position?.name,
                                name: experience.position?.name
                              }
                            }}
                            loadingMessage={() => 'Loading positions...'}
                            className={
                              meta.error && meta.touched
                                ? 'expert-bulk-create__dropdown-wrapper expert-bulk-create__dropdown-wrapper--error'
                                : 'expert-bulk-create__dropdown-wrapper'
                            }
                            classNamePrefix="expert-bulk-create__dropdown"
                          />
                        )}
                      </FastField>
                      {this.renderFormikError(
                        `experts.${expertIndex}.experiences.${experienceIndex}.position`
                      )}
                    </div>
                  </div>
                  <div className="expert-form__input-group">
                    <label className="expert-form__label">Company *</label>
                    <div className={'expert-form__input-wrapper'}>
                      <FastField
                        name={`experts.${expertIndex}.experiences.${experienceIndex}.company`}>
                        {({ field, form, meta }) => (
                          <AsyncCreatable
                            isSearchable={true}
                            isClearable={true}
                            loadOptions={this.getCompanySuggestionsDebounced}
                            onChange={async selectedOption => {
                              if (selectedOption) {
                                await setFieldValue(
                                  `experts.${expertIndex}.experiences.${experienceIndex}.company`,
                                  {
                                    id: selectedOption.value,
                                    name: selectedOption.label
                                  }
                                )
                              } else {
                                await setFieldValue(
                                  `experts.${expertIndex}.experiences.${experienceIndex}.company`,
                                  null
                                )
                              }
                              setFieldTouched(
                                `experts.${expertIndex}.experiences.${experienceIndex}.company`
                              )
                            }}
                            onCreateOption={async valueToCreate => {
                              if (valueToCreate) {
                                await setFieldValue(
                                  `experts.${expertIndex}.experiences.${experienceIndex}.company`,
                                  { id: valueToCreate, name: valueToCreate }
                                )

                                setFieldTouched(
                                  `experts.${expertIndex}.experiences.${experienceIndex}.company`
                                )
                              }
                            }}
                            value={{
                              label: experience.company?.name,
                              value: {
                                id: experience.company?.id
                                  ? experience.company?.id
                                  : experience.company?.name,
                                name: experience.company?.name
                              }
                            }}
                            loadingMessage={() => 'Loading companies...'}
                            className={
                              meta.error && meta.touched
                                ? 'expert-bulk-create__dropdown-wrapper expert-bulk-create__dropdown-wrapper--error'
                                : 'expert-bulk-create__dropdown-wrapper'
                            }
                            classNamePrefix="expert-bulk-create__dropdown"
                          />
                        )}
                      </FastField>
                      {this.renderFormikError(
                        `experts.${expertIndex}.experiences.${experienceIndex}.company`
                      )}
                    </div>
                  </div>
                  <div className="expert-form__input-group">
                    <label className="expert-form__label">Location</label>
                    <div className={'expert-form__input-wrapper'}>
                      <FastField
                        name={`experts.${expertIndex}.experiences.${experienceIndex}.location`}>
                        {({ field, form, meta }) => (
                          <AsyncSelect
                            isSearchable={true}
                            isClearable={true}
                            loadOptions={this.getCitySuggestionsDebounced}
                            onChange={async selectedOption => {
                              if (selectedOption) {
                                await setFieldValue(
                                  `experts.${expertIndex}.experiences.${experienceIndex}.location`,
                                  selectedOption.label
                                )
                              } else {
                                await setFieldValue(
                                  `experts.${expertIndex}.experiences.${experienceIndex}.location`,
                                  null
                                )
                              }
                              setFieldTouched(
                                `experts.${expertIndex}.experiences.${experienceIndex}.location`
                              )
                            }}
                            value={{
                              label: experience.location,
                              value: experience.location
                            }}
                            className={
                              meta.error && meta.touched
                                ? 'expert-bulk-create__dropdown-wrapper expert-bulk-create__dropdown-wrapper--error'
                                : 'expert-bulk-create__dropdown-wrapper'
                            }
                            classNamePrefix="expert-bulk-create__dropdown"
                          />
                        )}
                      </FastField>
                      {this.renderFormikError(
                        `experts.${expertIndex}.experiences.${experienceIndex}.location`
                      )}
                    </div>
                  </div>
                  <div className="expert-form__input-group">
                    <label className="expert-form__label">{'From-to *'}</label>
                    <div className={'expert-form__input-wrapper'}>
                      <Field>
                        {({ field, form, meta }) => {
                          const startDateFieldError =
                            get(
                              form.touched,
                              `experts.${expertIndex}.experiences.${experienceIndex}.startDate`,
                              false
                            ) &&
                            get(
                              form.errors,
                              `experts.${expertIndex}.experiences.${experienceIndex}.startDate`
                            )
                          const endDateFieldError =
                            get(
                              form.touched,
                              `experts.${expertIndex}.experiences.${experienceIndex}.endDate`,
                              false
                            ) &&
                            get(
                              form.errors,
                              `experts.${expertIndex}.experiences.${experienceIndex}.endDate`
                            )
                          return (
                            <div>
                              <div
                                className={
                                  'expert-form__input-wrapper expert-form__input-wrapper--inline'
                                }>
                                <MonthYearPickerRange
                                  fromError={
                                    form.touched.experts?.[expertIndex]
                                      ?.experiences?.[experienceIndex]
                                      ?.startDate &&
                                    form.errors.experts?.[expertIndex]
                                      ?.experiences?.[experienceIndex]
                                      ?.startDate
                                  }
                                  toError={
                                    form.touched.experts?.[expertIndex]
                                      ?.experiences?.[experienceIndex]
                                      ?.endDate &&
                                    form.errors.experts?.[expertIndex]
                                      ?.experiences?.[experienceIndex]?.endDate
                                  }
                                  monthYearFrom={experience.startDate}
                                  monthYearTo={experience.endDate}
                                  onFromChange={async value => {
                                    const selectedMonthAndYear = moment(
                                      value
                                    ).toISOString()
                                    const stigifiedDate = stringifyDate(
                                      selectedMonthAndYear
                                    )
                                    await setFieldValue(
                                      `experts.${expertIndex}.experiences.${experienceIndex}.startDate`,
                                      stigifiedDate
                                    )
                                    await setFieldTouched(
                                      `experts.${expertIndex}.experiences.${experienceIndex}.startDate`
                                    )
                                  }}
                                  onToChange={async value => {
                                    const selectedMonthAndYear = moment(
                                      value
                                    ).toISOString()
                                    await setFieldValue(
                                      `experts.${expertIndex}.experiences.${experienceIndex}.endDate`,
                                      stringifyDate(selectedMonthAndYear)
                                    )
                                    await setFieldValue(
                                      `experts.${expertIndex}.experiences.${experienceIndex}.current`,
                                      false
                                    )
                                    await setFieldTouched(
                                      `experts.${expertIndex}.experiences.${experienceIndex}.endDate`
                                    )
                                    await setFieldTouched(
                                      `experts.${expertIndex}.experiences.${experienceIndex}.current`
                                    )
                                  }}
                                  onFromBlur={() => {
                                    setFieldTouched(
                                      `experts.${expertIndex}.experiences.${experienceIndex}.startDate`
                                    )
                                  }}
                                  onToBlur={() => {
                                    setFieldTouched(
                                      `experts.${expertIndex}.experiences.${experienceIndex}.endDate`
                                    )
                                    setFieldTouched(
                                      `experts.${expertIndex}.experiences.${experienceIndex}.current`
                                    )
                                  }}
                                />
                                <Checkbox
                                  className="m-l-xl"
                                  value={experience.current}
                                  label="Current"
                                  onChange={async value => {
                                    await setFieldValue(
                                      `experts.${expertIndex}.experiences.${experienceIndex}.current`,
                                      value
                                    )
                                    if (value) {
                                      await setFieldValue(
                                        `experts.${expertIndex}.experiences.${experienceIndex}.endDate`,
                                        null
                                      )
                                    }
                                    await setFieldTouched(
                                      `experts.${expertIndex}.experiences.${experienceIndex}.current`
                                    )
                                    await setFieldTouched(
                                      `experts.${expertIndex}.experiences.${experienceIndex}.endDate`
                                    )
                                  }}
                                  onBlur={() => {
                                    setFieldTouched(
                                      `experts.${expertIndex}.experiences.${experienceIndex}.current`
                                    )
                                    setFieldTouched(
                                      `experts.${expertIndex}.experiences.${experienceIndex}.endDate`
                                    )
                                  }}
                                />
                              </div>
                              <div className="expert-form__input-wrapper expert-form__month-range">
                                <div className="expert-form__input-error start-date">
                                  {startDateFieldError}
                                </div>
                                <div className="expert-form__input-error end-date">
                                  {endDateFieldError}
                                </div>
                              </div>
                            </div>
                          )
                        }}
                      </Field>
                    </div>
                  </div>
                  <FastField
                    name={`experts.${expertIndex}.experiences.${experienceIndex}.description`}
                    label="Description"
                    placeholder="Type something"
                    toolbarRight
                    component={RichTextEditor}
                  />
                </div>
              ))}
              {expert.experiences.every(
                e =>
                  e.position &&
                  e.company &&
                  e.startDate &&
                  (e.endDate || e.current)
              ) && (
                <div className="expert-form__section">
                  <div className="expert-form__input-group">
                    <label className="expert-form__label">
                      <Button
                        secondary
                        onClick={() => {
                          experienceArrayHelpers.push(
                            createEmptyWorkExperienceObject()
                          )
                        }}>
                        Add experience
                      </Button>
                    </label>
                    <div className="expert-form__input-wrapper">
                      Expert's work experiences will automatically be organised
                      chronologically by start-end year
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        />
      </div>
    )
  }

  renderGetContactInfo = (
    expert,
    expertIndex,
    errors,
    setFieldValue,
    setFieldTouched,
    validateForm
  ) => {
    if (
      (!this.props.confirmFieldOptions.some(
        e =>
          e === ExpertOptionField.PhoneNumber || e === ExpertOptionField.Email
      ) &&
        (!this.state.expertErrors[expertIndex] ||
          !this.state.expertErrors[expertIndex].some(
            e =>
              e === ExpertOptionField.PhoneNumber ||
              e === ExpertOptionField.Email
          ))) ||
      !this.state.showGetContactInfoSectionForTrackingIds.includes(
        expert.bulkCreateTrackingId
      )
    ) {
      return
    }

    return (
      <div className="get-contact-info-form-section">
        <GetContactInfo
          expert={expert}
          staticData={this.props.staticData}
          onError={err => this.props.growlStore.showError(err)}>
          {({
            loading,
            phoneNumbers,
            emailAddresses,
            phoneToString,
            emailToString
          }) => {
            phoneNumbers = differenceBy(
              phoneNumbers,
              this.state.addedPhonesByTrackingId.find(
                e => e.id === expert.bulkCreateTrackingId
              )?.phones,
              phoneToString
            )

            emailAddresses = differenceBy(
              emailAddresses,
              this.state.addedEmailsByTrackingId.find(
                e => e.id === expert.bulkCreateTrackingId
              )?.emails,
              emailToString
            )

            if (loading) return <Spinner />
            return (
              <div className="contact-info-picker">
                {(this.props.confirmFieldOptions.includes(
                  ExpertOptionField.PhoneNumber
                ) ||
                  (this.state.expertErrors[expertIndex] &&
                    this.state.expertErrors[expertIndex].includes(
                      ExpertOptionField.PhoneNumber
                    ))) && (
                  <div className="contact-info-picker__section">
                    <h2 className="contact-info-picker__section-title">
                      Phone numbers
                    </h2>
                    {phoneNumbers.length ? (
                      <div>
                        {phoneNumbers.map(phone => (
                          <ContactInfoEntry
                            key={phoneToString(phone)}
                            item={phone}
                            itemToString={phoneToString}
                            onClick={phone => {
                              const { phoneNum, phoneTypeId } = phone
                              const { phones } = expert.address

                              let insertIndex = findIndex(
                                phones,
                                ({ phoneNum }) => !phoneNum
                              )

                              insertIndex =
                                insertIndex >= 0 ? insertIndex : phones.length

                              const phoneTypeIdPath = `experts.${expertIndex}.address.phones[${insertIndex}].phoneTypeId`
                              const phoneNumPath = `experts.${expertIndex}.address.phones[${insertIndex}].phoneNum`

                              setFieldValue(phoneTypeIdPath, phoneTypeId)
                              setFieldValue(phoneNumPath, phoneNum)

                              const foundPhones = this.state.addedPhonesByTrackingId.find(
                                e => e.id === expert.bulkCreateTrackingId
                              )
                              if (foundPhones) {
                                this.setState({
                                  addedPhonesByTrackingId: [
                                    ...this.state.addedPhonesByTrackingId,
                                    {
                                      id: foundPhones.id,
                                      phones: [...foundPhones.phones, phone]
                                    }
                                  ]
                                })
                              } else {
                                this.setState({
                                  addedPhonesByTrackingId: [
                                    ...this.state.addedPhonesByTrackingId,
                                    {
                                      id: expert.bulkCreateTrackingId,
                                      phones: [phone]
                                    }
                                  ]
                                })
                              }
                            }}
                          />
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray">
                        No phone numbers available
                      </span>
                    )}
                  </div>
                )}
                {(this.props.confirmFieldOptions.includes(
                  ExpertOptionField.Email
                ) ||
                  (this.state.expertErrors[expertIndex] &&
                    this.state.expertErrors[expertIndex].includes(
                      ExpertOptionField.Email
                    ))) && (
                  <div className="contact-info-picker__section">
                    <h2 className="contact-info-picker__section-title">
                      Email addresses
                    </h2>
                    {emailAddresses.length ? (
                      <div>
                        {emailAddresses.map(emailAddress => (
                          <ContactInfoEntry
                            key={emailToString(emailAddress)}
                            item={emailAddress}
                            itemToString={emailToString}
                            onClick={email => {
                              const { emails } = expert.address
                              let insertIndex = findIndex(
                                emails,
                                ({ email }) => !email
                              )

                              insertIndex =
                                insertIndex >= 0 ? insertIndex : emails.length

                              const emailPath = expert.email
                                ? `experts.${expertIndex}.address.emails[${insertIndex}].email`
                                : `experts.${expertIndex}.email`

                              setFieldValue(emailPath, email.email)

                              const foundEmails = this.state.addedEmailsByTrackingId.find(
                                e => e.id === expert.bulkCreateTrackingId
                              )
                              if (foundEmails) {
                                this.setState({
                                  addedEmailsByTrackingId: [
                                    ...this.state.addedEmailsByTrackingId,
                                    {
                                      id: foundEmails.id,
                                      phones: [
                                        ...foundEmails.emails,
                                        email.email
                                      ]
                                    }
                                  ]
                                })
                              } else {
                                this.setState({
                                  addedEmailsByTrackingId: [
                                    ...this.state.addedEmailsByTrackingId,
                                    {
                                      id: expert.bulkCreateTrackingId,
                                      emails: [email]
                                    }
                                  ]
                                })
                              }
                            }}
                          />
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray">
                        No email addresses available
                      </span>
                    )}
                  </div>
                )}
              </div>
            )
          }}
        </GetContactInfo>
      </div>
    )
  }

  renderContactInformation = (
    expert,
    expertIndex,
    errors,
    setFieldValue,
    setFieldTouched,
    validateForm
  ) => {
    if (
      !this.props.confirmFieldOptions.some(
        e =>
          e === ExpertOptionField.PhoneNumber ||
          e === ExpertOptionField.Email ||
          e === ExpertOptionField.Link
      ) &&
      (!this.state.expertErrors[expertIndex] ||
        !this.state.expertErrors[expertIndex].some(
          e =>
            e === ExpertOptionField.PhoneNumber ||
            e === ExpertOptionField.Email ||
            e === ExpertOptionField.Link
        ))
    ) {
      return
    }

    const hasName = expert.firstName && expert.lastName
    const hasCurrentExperience = expert.experiences.some(
      ({ current }) => !!current
    )

    return (
      <div className="expert-form__section">
        {(this.props.confirmFieldOptions.includes(ExpertOptionField.Email) ||
          this.props.confirmFieldOptions.includes(
            ExpertOptionField.PhoneNumber
          ) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.Email
            )) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.PhoneNumber
            ))) && (
          <h2 className="expert-form__subtitle expert-form__subtitle--with-action">
            Contact information
            {
              <div className="action-wrapper">
                <Action
                  disabled={!(hasName && hasCurrentExperience)}
                  onClick={() => {
                    if (
                      this.state.showGetContactInfoSectionForTrackingIds.includes(
                        expert.bulkCreateTrackingId
                      )
                    ) {
                      this.setState({
                        showGetContactInfoSectionForTrackingIds: this.state.showGetContactInfoSectionForTrackingIds.filter(
                          e => e !== expert.bulkCreateTrackingId
                        )
                      })
                    } else {
                      this.setState({
                        showGetContactInfoSectionForTrackingIds: [
                          ...this.state.showGetContactInfoSectionForTrackingIds,
                          expert.bulkCreateTrackingId
                        ]
                      })
                    }
                  }}>
                  <Lusha />
                  <span>Get contact info</span>
                </Action>
                <Tooltip tooltip="First name, last name, and at least one work experience marked current have to be defined before you can get contact info" />
              </div>
            }
          </h2>
        )}
        {this.renderGetContactInfo(expert, expertIndex, setFieldValue)}
        {(this.props.confirmFieldOptions.includes(ExpertOptionField.Email) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.Email
            ))) &&
          expert &&
          expert.phoneTypeId ===
            this.props.staticData.CONST.PHONE_TYPES.MOBILE && (
            <div className="expert-form__input-group">
              <label className="expert-form__label">{`Mobile (added by expert)`}</label>
              <div className="expert-form__input-wrapper">
                <div className="expert-form__repeatable-input-wrapper">
                  <div className="expert-form__repeatable-input expert-form__repeatable-input--multiple">
                    <FastField
                      name={`experts.${expertIndex}.expert.phoneNum`}
                      component={Input}
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        {(this.props.confirmFieldOptions.includes(
          ExpertOptionField.PhoneNumber
        ) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.PhoneNumber
            ))) && (
          <div className="expert-form__input-group">
            <label className="expert-form__label">
              <div>
                Phones
                <Tooltip
                  tooltip={this.props.staticData.phoneNumberTooltipText}
                />
              </div>
            </label>
            <div className="expert-form__input-wrapper">
              <FieldArray
                name={`experts.${expertIndex}.address.phones`}
                render={phoneArrayHelpers => (
                  <div className="expert-form__repeatable-input-wrapper">
                    {expert.address.phones.map((phone, phoneIndex) => (
                      <div
                        className="expert-form__repeatable-input expert-form__repeatable-input--multiple"
                        key={phoneIndex}>
                        <div className={'expert-form__input-wrapper'}>
                          <FastField
                            name={`experts.${expertIndex}.address.phones.${phoneIndex}.phoneTypeId`}>
                            {({ field, form, meta }) => (
                              <Select
                                placeholder="Select type"
                                options={this.props.staticData.phoneTypes.map(
                                  e => {
                                    return {
                                      label: e.name,
                                      value: e.id,
                                      isDisabled: phone.phoneTypeId === e.id
                                    }
                                  }
                                )}
                                onChange={async selectedValue => {
                                  if (selectedValue) {
                                    await setFieldValue(
                                      `experts.${expertIndex}.address.phones.${phoneIndex}.phoneTypeId`,
                                      selectedValue.value
                                    )

                                    setFieldTouched(
                                      `experts.${expertIndex}.address.phones.${phoneIndex}.phoneTypeId`
                                    )
                                  }
                                }}
                                value={(() => {
                                  if (phone.phoneTypeId) {
                                    const matchedPhoneType = this.props.staticData.phoneTypes.find(
                                      e => e.id === phone.phoneTypeId
                                    )
                                    return {
                                      label: matchedPhoneType.name,
                                      value: matchedPhoneType.phoneTypeId
                                    }
                                  }
                                })()}
                                className={
                                  meta.error && meta.touched
                                    ? 'expert-bulk-create__dropdown-wrapper expert-bulk-create__dropdown-wrapper--error'
                                    : 'expert-bulk-create__dropdown-wrapper'
                                }
                                classNamePrefix="expert-bulk-create__dropdown"
                              />
                            )}
                          </FastField>
                          {this.renderFormikError(
                            `experts.${expertIndex}.address.phones.${phoneIndex}.phoneTypeId`
                          )}
                        </div>
                        <FastField
                          name={`experts.${expertIndex}.address.phones.${phoneIndex}.phoneNum`}
                          inputClassName="expert-form__input"
                          component={Input}
                        />
                        <RepeatableAction
                          fieldValues={expert.address.phones}
                          arrayHelpers={phoneArrayHelpers}
                          index={phoneIndex}
                          allowAdd={expert.address.phones.every(
                            e => e.phoneTypeId && e.phoneNum
                          )}
                          initialObject={createEmptyPhoneObject()}
                          onAfterDelete={() => {
                            validateForm()
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              />
            </div>
          </div>
        )}
        {(this.props.confirmFieldOptions.includes(ExpertOptionField.Email) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.Email
            ))) && (
          <>
            <FastField
              name={`experts.${expertIndex}.email`}
              label={'Primary email'}
              component={Input}
              validate={value => {
                this.runLongLastingValidation = true
              }}
            />
            <div className="expert-form__input-group">
              <label className="expert-form__label">Additional emails</label>
              <div className="expert-form__input-wrapper">
                <FieldArray
                  name={`experts.${expertIndex}.address.emails`}
                  render={emailArrayHelpers => (
                    <div className="expert-form__repeatable-input-wrapper">
                      {expert.address.emails.map((email, emailIndex) => (
                        <div
                          className="expert-form__repeatable-input"
                          key={emailIndex}>
                          <FastField
                            name={`experts.${expertIndex}.address.emails.${emailIndex}.email`}
                            inputClassName="expert-form__input"
                            component={Input}
                            validate={value => {
                              this.runLongLastingValidation = true
                            }}
                          />
                          <RepeatableAction
                            fieldValues={expert.address.emails}
                            arrayHelpers={emailArrayHelpers}
                            index={emailIndex}
                            allowAdd={
                              !!(
                                expert.address.emails.every(e => e.email) &&
                                expert.email
                              )
                            }
                            initialObject={createEmptyEmailObject()}
                            onAfterDelete={() => {
                              validateForm()
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                />
              </div>
            </div>
          </>
        )}
        {(this.props.confirmFieldOptions.includes(ExpertOptionField.Link) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.Link
            ))) && (
          <div className="expert-form__input-group">
            <label className="expert-form__label">Proffesional links</label>
            <div className="expert-form__input-wrapper">
              <FieldArray
                name={`experts.${expertIndex}.links`}
                render={linkArrayHelpers => (
                  <div className="expert-form__repeatable-input-wrapper">
                    {expert.links.map((link, linkIndex) => (
                      <div
                        className="expert-form__repeatable-input"
                        key={linkIndex}>
                        <FastField
                          name={`experts.${expertIndex}.links.${linkIndex}.professionalLinkUrl`}
                          inputClassName="expert-form__input"
                          component={Input}
                        />
                        <RepeatableAction
                          fieldValues={expert.links}
                          arrayHelpers={linkArrayHelpers}
                          index={linkIndex}
                          allowAdd={expert.links.every(
                            e => e.professionalLinkUrl
                          )}
                          initialObject={createEmptyLinkObject()}
                          onAfterDelete={() => {
                            validateForm()
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  renderOther = (
    expert,
    expertIndex,
    errors,
    setFieldValue,
    setFieldTouched,
    validateForm
  ) => {
    if (
      !this.props.confirmFieldOptions.some(
        e =>
          e === ExpertOptionField.Source ||
          e === ExpertOptionField.NewsletterSubscribe ||
          e === ExpertOptionField.SendSmsReminders
      ) &&
      (!this.state.expertErrors[expertIndex] ||
        !this.state.expertErrors[expertIndex].some(
          e =>
            e === ExpertOptionField.Source ||
            e === ExpertOptionField.NewsletterSubscribe ||
            e === ExpertOptionField.SendSmsReminders
        ))
    ) {
      return
    }

    return (
      <div className="expert-form__section">
        {(this.props.confirmFieldOptions.includes(ExpertOptionField.Source) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.Source
            ))) && (
          <div className="expert-form__input-group">
            <label className="expert-form__label">Source</label>
            <div className={'expert-form__input-wrapper'}>
              <FastField name={`experts.${expertIndex}.cv.cvSource`}>
                {({ field, form, meta }) => (
                  <AsyncCreatable
                    isSearchable={true}
                    isClearable={true}
                    loadOptions={this.getCvSourceSuggestionsDebounced}
                    onChange={async selectedOption => {
                      if (selectedOption) {
                        await setFieldValue(
                          `experts.${expertIndex}.cv.cvSource`,
                          {
                            name: selectedOption.label,
                            id: selectedOption.id,
                            active: true
                          }
                        )
                      } else {
                        await setFieldValue(
                          `experts.${expertIndex}.cv.cvSource`,
                          null
                        )
                      }
                      setFieldTouched(`experts.${expertIndex}.cv.cvSource`)
                    }}
                    onCreateOption={async valueToCreate => {
                      if (valueToCreate) {
                        await setFieldValue(
                          `experts.${expertIndex}.cv.cvSource`,
                          {
                            name: valueToCreate,
                            id: valueToCreate,
                            isCreatable: true,
                            active: true
                          }
                        )

                        setFieldTouched(`experts.${expertIndex}.cv.cvSource`)
                      }
                    }}
                    value={{
                      label: expert.cv?.cvSource?.name,
                      value: {
                        id: expert.cv?.cvSource?.id
                          ? expert.cv.cvSource.id
                          : expert.cv?.cvSource?.name,
                        name: expert.cv?.cvSource?.name,
                        active: expert.cv?.cvSource?.active
                      }
                    }}
                    loadingMessage={() => 'Loading CV sources...'}
                    className={
                      meta.error && meta.touched
                        ? 'expert-bulk-create__dropdown-wrapper expert-bulk-create__dropdown-wrapper--error'
                        : 'expert-bulk-create__dropdown-wrapper'
                    }
                    classNamePrefix="expert-bulk-create__dropdown"
                  />
                )}
              </FastField>
              {this.renderFormikError(`experts.${expertIndex}.cv.cvSource`)}
            </div>
          </div>
        )}
        {(this.props.confirmFieldOptions.includes(
          ExpertOptionField.NewsletterSubscribe
        ) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.NewsletterSubscribe
            ))) && (
          <div>
            <FastField
              label="Subscribe to newsletter"
              onChange={value => {
                setFieldValue(`experts.${expertIndex}.expert.newsletter`, value)
              }}
              onBlur={() => {
                setFieldTouched(`experts.${expertIndex}.expert.newsletter`)
              }}
              value={expert.expert?.newsletter}
              component={Checkbox}
            />
          </div>
        )}
        {(this.props.confirmFieldOptions.includes(
          ExpertOptionField.SendSmsReminders
        ) ||
          (this.state.expertErrors[expertIndex] &&
            this.state.expertErrors[expertIndex].includes(
              ExpertOptionField.SendSmsReminders
            ))) && (
          <div>
            <FastField
              label="Interview reminders via SMS"
              tooltip="SMS will only be sent to numbers saved as mobile phone"
              onChange={value => {
                setFieldValue(
                  `experts.${expertIndex}.expert.sendSmsReminders`,
                  value
                )
              }}
              onBlur={() => {
                setFieldTouched(
                  `experts.${expertIndex}.expert.sendSmsReminders`
                )
              }}
              value={expert.expert?.sendSmsReminders}
              component={Checkbox}
              disabled={
                !expert.address.phones.some(
                  e =>
                    e.phoneNum &&
                    e.phoneTypeId ===
                      this.props.staticData.CONST.PHONE_TYPES.MOBILE
                )
              }
            />
          </div>
        )}
      </div>
    )
  }

  validateForm = async values => {
    const errors = {
      experts: []
    }

    for (const [expertIndex, expert] of values.experts.entries()) {
      if (!this.state.expertErrors[expertIndex]) {
        this.setState((prevState, props) => {
          let newExpertErrors = toJS(prevState.expertErrors)
          newExpertErrors.splice(expertIndex, 0, [])

          return {
            expertErrors: newExpertErrors
          }
        })
      }
    }

    const validationPromises = []

    for (const [expertIndex, expert] of values.experts.entries()) {
      validationPromises.push(
        new Promise(async (resolve, reject) => {
          errors.experts[expertIndex] = {
            bulkCreateTrackingId: expert.bulkCreateTrackingId,
            hasError: false,
            languages: [],
            expert: {},
            address: {
              phones: [],
              emails: []
            },
            experiences: [],
            links: [],
            cv: {}
          }

          const expertValidationResult = await this.props.expertBulkCreateStore.validateExpert(
            expert,
            this.props.requiredFieldOptions,
            values.experts,
            this.runLongLastingValidation
          )
          if (expertValidationResult?.hasError) {
            errors.experts[expertIndex] = expertValidationResult

            this.state.expertErrors[expertIndex] = uniq([
              ...expertValidationResult.errorFields,
              ...this.state.expertErrors[expertIndex]
            ])
          }

          resolve()
        })
      )
    }

    await Promise.all(validationPromises)

    this.runLongLastingValidation = false

    if (errors.experts.some(e => e.hasError)) {
      return errors
    } else {
      return {}
    }
  }

  render() {
    return (
      <div>
        <ArrowUpwardRounded
          onClick={() => {
            if (this.props.scrollToTop) {
              this.props.scrollToTop(true)
            }
          }}
          className="expert-bulk-create__navigation-button expert-bulk-create__navigation-button--up"
          style={{
            left: this.state.leftBoundForRightColumn,
            top: this.state.scrollUpTop
          }}
        />

        <ArrowDownwardRounded
          onClick={() => {
            if (this.props.scrollToBottom) {
              this.props.scrollToBottom(true)
            }
          }}
          className="expert-bulk-create__navigation-button expert-bulk-create__navigation-button--down"
          style={{
            left: this.state.leftBoundForRightColumn,
            top: this.state.scrollDownTop
          }}
        />

        <Formik
          innerRef={this.formikRef}
          initialValues={(() => {
            if (this.props.scrollToTop) {
              this.props.scrollToTop()
            }

            return { experts: toJS(this.props.expertsToProcess) }
          })()}
          initialErrors={(() => {
            const initialErrors = toJS(this.props.initialValidationErrors)
            this.state.expertErrors = initialErrors.map(e => e.errorFields)
            return { experts: initialErrors }
          })()}
          initialTouched={(() => {
            if (
              this.props.highlightErrorsOnInitialize &&
              this.props.initialValidationErrors
            ) {
              const getTouchedObj = errors => {
                if (Array.isArray(errors)) {
                  const touched = []
                  errors.map(val => {
                    touched.push(getTouchedObj(val))
                  })
                  return touched
                }
                const touched = {}
                Object.keys(errors).map(key => {
                  if (Array.isArray(errors[key])) {
                    errors[key].map((val, index) => {
                      if (index == 0) touched[key] = []
                      const ret = getTouchedObj(val)
                      touched[key].push(ret)
                    })
                  } else if (typeof errors[key] !== 'string') {
                    touched[key] = getTouchedObj(errors[key])
                  } else {
                    touched[key] = true
                  }
                })

                return touched
              }

              return {
                experts: getTouchedObj(toJS(this.props.initialValidationErrors))
              }
            }
          })()}
          onSubmit={values => {
            this.props.expertBulkCreateStore.expertFinalizeQueue
              .get(this.props.processingBulkId)
              .expertsToSave.push(...values.experts)
            this.props.modalStore.close()
          }}
          validateOnChange={false}
          validateOnBlur={true}
          validateOnMount={false}
          validate={async values => {
            const validationResult = await this.validateForm(values)
            if (this.firstValidate) {
              this.firstValidate = false
            }
            return validationResult
          }}
          enableReinitialize={true}>
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            validateField,
            submitForm,
            isValidating,
            handleSubmit
          }) => {
            return (
              <Form>
                <FieldArray
                  name="experts"
                  render={expertArrayHelpers => (
                    <div>
                      {values.experts.map((expert, expertIndex) => (
                        <div key={expertIndex}>
                          <div
                            className={
                              expertIndex > 0
                                ? 'expert-form__input-group expert-bulk-create__header'
                                : 'expert-form__input-group'
                            }>
                            <h1>
                              {expert.fullName
                                ? expert.fullName
                                : 'Name not set'}
                            </h1>
                            {values.experts.length > 1 && (
                              <Button
                                primary
                                className={
                                  'expert-bulk-create__header-button pull-right'
                                }
                                onClick={() => {
                                  expertArrayHelpers.remove(expertIndex)

                                  let newExpertErrors = toJS(
                                    this.state.expertErrors
                                  )
                                  newExpertErrors.splice(expertIndex, 1)
                                  this.state.expertErrors = newExpertErrors

                                  this.props.expertBulkCreateStore.assignProcessingStatus(
                                    expert.bulkCreateTrackingId,
                                    this.props.processingBulkId,
                                    ExpertProcessingStatus.Cancelled,
                                    '',
                                    toJS(expert),
                                    expert.bulkCreateCustomIdentifier
                                  )

                                  if (
                                    values.experts.filter(
                                      e =>
                                        e.bulkCreateTrackingId !=
                                        expert.bulkCreateTrackingId
                                    ).length === 0
                                  ) {
                                    this.props.modalStore.close()
                                  }
                                }}>
                                Discard Expert
                              </Button>
                            )}
                          </div>
                          {this.renderPersonalInformation(
                            expert,
                            expertIndex,
                            errors,
                            setFieldValue,
                            setFieldTouched,
                            validateField
                          )}
                          {this.renderLocation(
                            expert,
                            expertIndex,
                            errors,
                            setFieldValue,
                            setFieldTouched,
                            validateField
                          )}
                          {this.renderProfessionalInformation(
                            expert,
                            expertIndex,
                            errors,
                            setFieldValue,
                            setFieldTouched,
                            validateField
                          )}
                          {this.renderWorkExperience(
                            expert,
                            expertIndex,
                            errors,
                            setFieldValue,
                            setFieldTouched,
                            validateField
                          )}
                          {this.renderContactInformation(
                            expert,
                            expertIndex,
                            errors,
                            setFieldValue,
                            setFieldTouched,
                            validateField
                          )}
                          {this.renderOther(
                            expert,
                            expertIndex,
                            errors,
                            setFieldValue,
                            setFieldTouched,
                            validateField
                          )}
                        </div>
                      ))}
                      <div className="expert-form__section p-b-0">
                        <Button
                          primary
                          submit
                          onClick={() => {
                            this.runLongLastingValidation = true
                          }}
                          disabled={isSubmitting}>
                          Save
                        </Button>
                        <Button
                          onClick={() => {
                            values.experts.forEach((expert, expertIndex) => {
                              this.props.expertBulkCreateStore.assignProcessingStatus(
                                expert.bulkCreateTrackingId,
                                this.props.processingBulkId,
                                ExpertProcessingStatus.Cancelled,
                                '',
                                toJS(expert),
                                expert.bulkCreateCustomIdentifier
                              )
                            })

                            this.props.modalStore.close()
                          }}>
                          {values.experts.length == 1
                            ? 'Discard'
                            : 'Discard all'}
                        </Button>
                        {values.experts.length > 1 &&
                          errors.experts?.some(e => e.hasError) && (
                            <Button
                              primary
                              red
                              className={'pull-right'}
                              onClick={() => {
                                const errIndexes = []
                                errors.experts.forEach((err, errIndex) => {
                                  if (err.hasError) {
                                    errIndexes.push(errIndex)

                                    const foundExpert = values.experts.find(
                                      e =>
                                        e.bulkCreateTrackingId ===
                                        err.bulkCreateTrackingId
                                    )

                                    this.props.expertBulkCreateStore.assignProcessingStatus(
                                      err.bulkCreateTrackingId,
                                      this.props.processingBulkId,
                                      ExpertProcessingStatus.Cancelled,
                                      '',
                                      toJS(foundExpert),
                                      err.bulkCreateCustomIdentifier
                                    )
                                  }
                                })

                                // We have to remove experts in a reverse direction as removing from the front would change indexes on every expert removed
                                let newExpertErrors = toJS(
                                  this.state.expertErrors
                                )
                                errIndexes.reverse().forEach(err => {
                                  expertArrayHelpers.remove(err)

                                  newExpertErrors.splice(err, 1)
                                })

                                this.state.expertErrors = newExpertErrors

                                // values.experts is not updated yet here
                                if (
                                  values.experts.length - errIndexes.length ===
                                  0
                                ) {
                                  this.props.modalStore.close()
                                }
                              }}>
                              Discard incomplete experts
                            </Button>
                          )}
                      </div>
                    </div>
                  )}
                />
              </Form>
            )
          }}
        </Formik>
      </div>
    )
  }
}

ExpertBulkCreate.defaultProps = {
  showCloseIcon: false,
  shouldCloseOnOverlayClick: false,
  hideButtons: true,
  confirmFieldOptions: [],
  highlightErrorsOnInitialize: true
}

export default withRouter(
  inject(
    'staticData',
    'modalStore',
    'growlStore',
    'expertStore',
    'expertSearchStore',
    'expertBulkCreateStore',
    'userStore'
  )(observer(ExpertBulkCreate))
)
